import React, {useEffect, useMemo, useRef, useState} from 'react';
import PositionFormProvider, {usePositionForm} from '../../providers/PositionFormProvider';
import ProjectFormProvider, {useProjectForm} from '../../providers/ProjectFormProvider';
import {useAuth} from '../../providers/AuthProvider';
import ProjectPosition from '../../models/ProjectPosition';
import Project from '../../models/Project';
import UserDiscipline from '../../models/UserDiscipline';
import LoadingSpinner from '../../components/LoadingSpinner';
import Form, {FormInputComponent} from '../../components/form/Form';
import FlipMove from 'react-flip-move';
import UserImageCircle from '../../components/UserImageCircle';
import {Img} from 'react-image';
import {PaperClipOutlined, PictureOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {useMutation, useQueryClient} from '@tanstack/react-query';

// Position step components
import PositionTiming from '../project-position/position-form/PositionTiming';
import {Input} from "formsy-react-components";
import SpinLoader from "../../components/SpinLoader";
import ReactDOM from "react-dom";
import {useAPI} from "../../config/API";
import TalentCandidate from "../../models/TalentCandidate";

/**
 * Supported flows for this wizard.
 *
 * - single-talent : "Quick Hire" from Browse Talent. Role/skills/industries/brands are
 *                   pre-selected from the client's search filters. Talent is invited to
 *                   shortlist and submit a proposal.
 * - talent-pool   : "Open Req" from a Talent Pool. The minimum discipline set covering the
 *                   pool is pre-selected and all matching pool members are invited.
 * - rehire-talent : "Rehire" an in-network (bring-your-own) talent. Records an engagement the
 *                   client already agreed to off-platform. NOTHING is pre-selected — the client
 *                   picks which of the talent's own Wripple Roles applies — and budget/pricing
 *                   is a recording of the already-agreed rate (defaulted to the talent's
 *                   standard rate). The proposal round-trip is skipped and the project moves
 *                   straight to "SOW Requested".
 */
export const QuickCreateTypes = {
    SingleTalent: 'single-talent',
    TalentPool: 'talent-pool',
    Rehire: 'rehire-talent',
};

/** Flows that target one specific talent user (as opposed to an entire pool) */
const isSingleTalentType = (type) => type === QuickCreateTypes.SingleTalent || type === QuickCreateTypes.Rehire;

/** The "record an already-agreed engagement" flow */
const isRehireType = (type) => type === QuickCreateTypes.Rehire;

/** Noun used throughout the copy for the thing being created */
const nounFor = (type) => {
    if (isRehireType(type)) return 'engagement';
    if (type === QuickCreateTypes.TalentPool) return 'req';
    return 'project';
};

const DEFAULT_STEPS = [
    {key: 'start', label: 'Get Started', icon: 'fas fa-bolt'},
    {key: 'basic-info', label: 'Role & Job Description', icon: 'fas fa-user-tag'},
    {key: 'timing-and-budget', label: 'Timing & Budget', icon: 'fas fa-clock'},
    {key: 'review', label: 'Review', icon: 'fas fa-check-circle'},
];

// Kept deliberately short so the inline wizard progress fits on one line in the modal header.
const REHIRE_STEPS = [
    {key: 'start', label: 'Get Started', icon: 'fas fa-handshake'},
    {key: 'basic-info', label: 'Role & Scope', icon: 'fas fa-user-tag'},
    {key: 'timing-and-budget', label: 'Agreed Terms', icon: 'fas fa-file-invoice-dollar'},
    {key: 'review', label: 'Review', icon: 'fas fa-check-circle'},
];

const stepsFor = (type) => isRehireType(type) ? REHIRE_STEPS : DEFAULT_STEPS;

/** Short display name for a talent user, e.g. "Jimmy C." */
const talentShortName = (talent) => {
    if (talent?.firstNamePlusInitial?.()) return talent.firstNamePlusInitial();

    const lastInitial = talent?.last_name?.[0] ? `${talent.last_name[0]}.` : '';
    return TB.trim(`${talent?.first_name || ''} ${lastInitial}`);
};

/**
 * Default title for a recorded rehire.
 *
 * Prefilled rather than left blank so that anyone who later sees this engagement — the talent in
 * their invite, Wripple ops in the SOW queue, finance on the invoice — can tell at a glance that
 * the terms were settled off-platform. The client can overwrite it.
 */
const rehireDefaultTitle = (talent) => {
    const name = talentShortName(talent);
    return name ? `Pre-Agreed Engagement — ${name}` : 'Pre-Agreed Engagement';
};

/**
 * The talent's standard bill rate — the figure shown on their tile in the pool.
 *
 * standardPlatformRate() returns any client-preferred rate negotiated for this talent ahead of
 * their list rate, which is the number an in-network rehire should default to. Falls back to the
 * highest platform rate across the selected roles for a talent with no aggregate rate on record.
 *
 * Bill-rate scale, matching position.target_rate.
 *
 * @param {Object} talent
 * @param {Array}  disciplines - only consulted by the fallback
 * @param {number} markup
 * @returns {number|null}
 */
const talentStandardBillRate = (talent, disciplines = [], markup = 0) => {
    if (!talent) return null;

    const tileRate = parseFloat(talent.standardPlatformRate?.(false));
    if (tileRate && !TB.isNaN(tileRate)) {
        return Math.ceil(tileRate);
    }

    const matched = talent.matchedDisciplines?.(disciplines || []) || [];
    if (!matched.length) return null;

    const rates = TB.filter(
        TB.map(matched, dis => (
            parseFloat(dis.platform_rate)
            || UserDiscipline.rateForDiscipline(dis.id, parseFloat(markup || 0), true)
        )),
        rate => !!rate && !TB.isNaN(rate)
    );

    if (!rates.length) return null;
    return Math.ceil(TB.max(rates));
};

/**
 * Prompt appended to the default rehire scope of work. It's guidance, not content — if the client
 * never replaces it, stripRehireScopePrompt() removes it on submit so it can't leak onto the SOW.
 */
const REHIRE_SCOPE_PROMPT = 'Replace this line with the specific deliverables and responsibilities you agreed to.';

/**
 * Default scope-of-work copy for a recorded rehire. Stored as HTML because the description field
 * is a rich-text editor (draft-js parses the markup back into blocks on load).
 */
const rehireDefaultDescription = (talent) => {
    const name = talentShortName(talent) || 'this talent';
    const clientOrgName = TB.auth().user()?.clientOrg?.()?.name || 'The client';

    return [
        '<p><strong>This is an existing engagement being recorded — not a new opportunity.</strong> ',
        `${clientOrgName} and ${name} agreed to this work directly, outside of the Wripple platform. `,
        'The role, scope, timing and rate captured here reflect terms both parties have already ',
        'accepted, so no proposal or bidding round is required.</p>',
        '<p>Wripple is engaged as Employer of Record to issue the SOW and handle invoicing and payment.</p>',
        `<p><em>${REHIRE_SCOPE_PROMPT}</em></p>`,
    ].join('');
};

/**
 * Removes the "replace this line" prompt from a rehire scope of work.
 *
 * Matched on normalized text rather than exact markup, because the description round-trips through
 * draft-js on every keystroke and comes back with re-serialized HTML. If the client already wrote
 * over the prompt there's nothing to match and this is a no-op. If stripping would empty the field
 * entirely, the original is kept so we never submit a blank scope.
 */
const stripRehireScopePrompt = (html) => {
    if (!html || !TB.includes(html, 'Replace this line')) {
        return html;
    }

    const normalize = (str) => TB.trim((str || '').replace(/ /g, ' ').replace(/\s+/g, ' '));
    const target = normalize(REHIRE_SCOPE_PROMPT);

    try {
        const doc = new DOMParser().parseFromString(`<div id="scope-root">${html}</div>`, 'text/html');
        const root = doc.getElementById('scope-root');

        Array.from(root.querySelectorAll('p, div, li')).forEach(node => {
            if (normalize(node.textContent) === target) {
                node.remove();
            }
        });

        const stripped = root.innerHTML;

        // Guard: if the prompt was the only content, keep what the client actually sees.
        return normalize(root.textContent) ? stripped : html;
    } catch (e) {
        console.warn('Could not strip rehire scope prompt', e);
        return html;
    }
};

const ImportanceLevelOptions = TB.map(laravel.importance_levels, (label, value) => ({
    label,
    value,
    isDefault: value === 2,
    level: value
}))

/**
 * Find the minimum set of disciplines that covers all users in the pool
 * Uses a greedy algorithm to select disciplines that cover the most users
 *
 * @param {Object} poolDisciplines - Format: {discipline_id: [user_ids]}
 * @param {Array} allUserIds - All user IDs in the pool
 * @returns {Array} - Array of discipline IDs that cover all users
 */
const findMinimumDisciplineSet = (poolDisciplines, allUserIds) => {
    if (!poolDisciplines || Object.keys(poolDisciplines).length === 0) {
        return [];
    }

    const uncoveredUsers = new Set(allUserIds);
    const selectedDisciplineIds = [];

    // Greedy algorithm: repeatedly select the discipline that covers the most uncovered users
    while (uncoveredUsers.size > 0) {
        let bestDisciplineId = null;
        let maxCoverage = 0;

        // Find discipline that covers the most uncovered users
        for (const [disciplineId, userIds] of Object.entries(poolDisciplines)) {
            const coverageCount = userIds.filter(userId => uncoveredUsers.has(userId)).length;
            if (coverageCount > maxCoverage) {
                maxCoverage = coverageCount;
                bestDisciplineId = parseInt(disciplineId);
            }
        }

        if (bestDisciplineId === null) {
            // No more disciplines can cover remaining users
            break;
        }

        // Add this discipline to selected set
        selectedDisciplineIds.push(bestDisciplineId);

        // Remove covered users from uncovered set
        const coveredUsers = poolDisciplines[bestDisciplineId];
        coveredUsers.forEach(userId => uncoveredUsers.delete(userId));
    }

    return selectedDisciplineIds;
};

/**
 * Count how many unique users are covered by the selected disciplines
 *
 * @param {Object} poolDisciplines - Format: {discipline_id: [user_ids]}
 * @param {Array} selectedDisciplineIds - Array of selected discipline IDs
 * @returns {number} - Count of unique users
 */
const countUsersForDisciplines = (poolDisciplines, selectedDisciplineIds) => {
    if (!poolDisciplines || !selectedDisciplineIds || selectedDisciplineIds.length === 0) {
        return 0;
    }

    const userSet = new Set();
    selectedDisciplineIds.forEach(disciplineId => {
        const userIds = poolDisciplines[disciplineId] || [];
        userIds.forEach(userId => userSet.add(userId));
    });

    return userSet.size;
};

/**
 * Builds initial position data pre-filled from browse search filters or talent pool.
 */
const buildInitialPosition = (data, searchFilters, type) => {
    let disciplines = [];
    let skills = [];
    let industries = [];
    let brands = [];

    const defaultLocation = TB.auth().user()?.clientOrg()?.location ||  TB.auth().user()?.location;

    if (type === 'single-talent') {
        const talent = data;

        // Pre-fill from search filters
        disciplines = searchFilters?.disciplines?.length ? searchFilters.disciplines : [];
        skills = searchFilters?.skills?.length ? searchFilters.skills : [];
        industries = searchFilters?.industries?.length ? searchFilters.industries : [];
        brands = searchFilters?.brands?.length ? searchFilters.brands : [];

        TB.map(searchFilters, (value, key) => {
            if (key.includes('skills')) {
                let scorePrefix = TB.replace(key, '_skills', '');
                let matchedImportance = TB.find(ImportanceLevelOptions, option => scorePrefix === TB.slugify(option.label));
                if (scorePrefix && matchedImportance) {
                    let skillsToAdd = TB.map(searchFilters[key], skill => ({...skill, importance: matchedImportance.level}));
                    skills = [...skills, ...skillsToAdd]
                }
            }
            if (key.includes('industries')) {
                let scorePrefix = TB.replace(key, '_industries', '');
                let matchedImportance = TB.find(ImportanceLevelOptions, option => scorePrefix === TB.slugify(option.label));
                if (scorePrefix && matchedImportance) {
                    let industriesToAdd = TB.map(searchFilters[key], industry => ({...industry, importance: matchedImportance.level}));
                    industries = [...industries, ...industriesToAdd]
                }
            }
        })

        // Filter to only include talent's skills/industries/brands/disciplines
        let talentSkillsById = TB.map(talent.skills, 'id');
        skills = TB.filter(skills, skill => talentSkillsById.includes(skill.id));
        let talentIndustriesById = TB.map(talent.industries_worked, 'id');
        industries = TB.filter(industries, industry => talentIndustriesById.includes(industry.id));
        let brandsById = TB.map(talent.brands, 'id');
        brands = TB.filter(brands, brand => brandsById.includes(brand.id));
        let talentDisciplinesById = TB.map(talent.talent_disciplines, 'id');
        disciplines = TB.filter(disciplines, dis => talentDisciplinesById.includes(dis.id))

        let matchedTalentDisciplineIds = TB.map(talent.matching?.disciplines?.disciplines || [], 'discipline_id')

        let matchingTemplates = TB.filter(TB.flatMap(laravel.position_template_categories, 'templates'), template => {
            return TB.find(template.disciplines, dis => matchedTalentDisciplineIds.includes(dis.id))
        }) || []

        const disciplineNames = TB.map(TB.take(disciplines, 2), 'name').join(', ');
        const skillNames = TB.take(TB.map(skills, 'name'), 3).join(', ');

        let description = `Seeking a talented professional`;
        if (disciplineNames) {
            description += ` with expertise as ${disciplineNames}`;
        }
        if (skillNames) {
            description += `. Key skills include ${skillNames}`;
        }
        description += '.';

        return ProjectPosition.make({
            title: disciplineNames,
            description,
            disciplines,
            skills,
            industries,
            brands,
            location: defaultLocation,
            templates: matchingTemplates,
            wants_junior_experience: true,
            wants_intermediate_experience: true,
            wants_senior_experience: true,
        });
    } else if (isRehireType(type)) {
        // ─────────────────────────────────────────────────────────────────────────────
        // REHIRE / "record an already-agreed engagement"
        //
        // Deliberately NO defaults for disciplines, skills, industries or brands. Unlike a
        // Browse Talent Quick Hire (which seeds from the client's search filters), the client
        // is recording work they have already scoped and agreed to with this talent. They pick
        // which of the talent's own `talent_disciplines` applies, and everything else is
        // written from scratch.
        //
        // Title and description ARE prefilled — but with boilerplate that flags the engagement as
        // pre-agreed, not with anything inferred about the work itself. See rehireDefaultTitle /
        // rehireDefaultDescription.
        // ─────────────────────────────────────────────────────────────────────────────
        return ProjectPosition.make({
            title: rehireDefaultTitle(data),
            description: rehireDefaultDescription(data),
            disciplines: [],
            skills: [],
            industries: [],
            brands: [],
            location: defaultLocation,
            templates: [],
            // Seed the agreed rate here rather than leaving it to the prefill effect further down.
            // The effect only runs after the form model already exists, which is too late for the
            // timing inputs: PositionTimeMaterialsForm reads target_rate in a useState initializer
            // and the retainer derives its whole pricing mode from whether target_rate is set, so
            // both latch their state on mount. Setting it at build time means the value is present
            // before any of them render — the banner and the input then agree, and a retainer opens
            // in rate-based mode already showing the talent's rate.
            target_rate: talentStandardBillRate(data),
            // The rate isn't a negotiating position here, it's the number both sides settled on —
            // always visible to the talent. The input for this is hidden in the rehire flow, so it
            // is set here rather than left to the (rate-dependent) prefill effect.
            target_rate_is_visible: true,
            wants_junior_experience: true,
            wants_intermediate_experience: true,
            wants_senior_experience: true,
        });
    } else if (type === 'talent-pool') {
        const talentPool = data;
        const settings = talentPool?.defaultSearchParams();

        let {
            skills,
            brands,
            industries
        } = settings || {skills: [], brands: [], industries: []}
        const poolDisciplines = talentPool.pool_disciplines || {};

        // get actual brands bc brands is saved in settings as an array of ids
        if (brands?.length && !TB.isNaN(TB.parseInt(brands[0]))) {

            brands = TB.filter(laravel.talent_brands || [], brand => brands.includes(brand.id));
        }
        // Get all user IDs in the pool
        const allUserIds = TB.uniq(TB.flatMap(talentPool.talent_disciplines, 'talent_users'));

        // Find minimum set of disciplines that covers all users
        let selectedDisciplineIds = [];
        let matchingTemplates = [];
        if (Object.keys(poolDisciplines).length > 0 && allUserIds.length > 0) {
            selectedDisciplineIds = findMinimumDisciplineSet(poolDisciplines, allUserIds);
        }

        // Get the actual discipline objects for the selected IDs
        if (selectedDisciplineIds.length > 0) {
            let allDisciplines = TB.map(talentPool.talent_disciplines, dis => TB.pick(dis, ['id', 'name']));
            disciplines = TB.uniqBy(
                TB.filter(allDisciplines, dis => selectedDisciplineIds.includes(dis.id)),
                'id'
            );
            console.log('disciplines', disciplines);


            const matchedTalentDisciplineIds = TB.map(disciplines, 'id')
            matchingTemplates = TB.filter(TB.flatMap(laravel.position_template_categories, 'templates'), template => {
                return TB.find(template.disciplines, dis => matchedTalentDisciplineIds.includes(dis.id))
            }) || []
        }
        const disciplineNames = TB.map(TB.take(disciplines, 2), 'name').join(', ');

        let description = `Seeking talented professionals from ${talentPool.name}`;
        if (disciplineNames) {
            description += ` with expertise as ${disciplineNames}`;
        }
        description += '.';

        return ProjectPosition.make({
            title: `${talentPool.name} - Open Req`,
            description,
            disciplines,
            skills,
            industries,
            brands,
            location: defaultLocation,
            templates: matchingTemplates,
            wants_junior_experience: true,
            wants_intermediate_experience: true,
            wants_senior_experience: true,
        });
    }
};

/**
 * Wrapper component that provides ProjectFormProvider and conditionally PositionFormProvider
 */
const QuickCreateProjectModal = ({
    type = 'single-talent',
    data,
    searchFilters = {},
    talentPool = null,
    steps = null,
    renderStep = null,
    isStepValid = null,
    extraParams = null,
    onCustomSubmit = null,
    submitLabel = null,
    headerTitle = null,
    headerSubtitle = null,
    successTitle = null,
    successMessage = null,
    onSuccess,
    onClose,
}) => {
    return (
        <ProjectFormProvider projectId="new">
            <QuickCreateProjectModalWrapper
                type={type}
                data={data}
                searchFilters={searchFilters}
                talentPool={talentPool}
                steps={steps}
                renderStep={renderStep}
                isStepValid={isStepValid}
                extraParams={extraParams}
                onCustomSubmit={onCustomSubmit}
                submitLabel={submitLabel}
                headerTitle={headerTitle}
                headerSubtitle={headerSubtitle}
                successTitle={successTitle}
                successMessage={successMessage}
                onSuccess={onSuccess}
                onClose={onClose}
            />
        </ProjectFormProvider>
    );
};

/**
 * Intermediate wrapper that waits for project to be defined before initializing PositionFormProvider
 */
const QuickCreateProjectModalWrapper = ({
    type, data, searchFilters, talentPool,
    steps, renderStep, isStepValid, extraParams, onCustomSubmit, submitLabel,
    headerTitle, headerSubtitle, successTitle, successMessage,
    onSuccess, onClose,
}) => {
    const {project, isLoading} = useProjectForm();

    if (isLoading || !project) {
        return (
            <div className="flex items-center justify-center p-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <PositionFormProvider positionId="new">
            <QuickCreateProjectModalContent
                type={type}
                data={data}
                searchFilters={searchFilters}
                talentPool={talentPool}
                steps={steps}
                renderStep={renderStep}
                isStepValid={isStepValid}
                extraParams={extraParams}
                onCustomSubmit={onCustomSubmit}
                submitLabel={submitLabel}
                headerTitle={headerTitle}
                headerSubtitle={headerSubtitle}
                successTitle={successTitle}
                successMessage={successMessage}
                onSuccess={onSuccess}
                onClose={onClose}
            />
        </PositionFormProvider>
    );
};

/**
 * Main modal content that uses useProjectForm and usePositionForm hooks
 */
const QuickCreateProjectModalContent = ({
    type = 'single-talent',
    data,
    searchFilters = {},
    talentPool = null,
    // ─── Extension points, all optional ───
    // A wrapper can drive its own flow through this modal without forking it. Every one of these
    // defaults to null, so existing callers behave exactly as before.
    //
    //   steps          replaces the built-in step list for this flow. May change between renders,
    //                  which is how a branching flow (e.g. Rehire's Change Order path) swaps its
    //                  steps once the user picks a direction.
    //   renderStep     (stepKey, ctx) => node | null. Return a node to own that step's body;
    //                  return null to fall through to the built-in step of that key.
    //   isStepValid    (stepKey, ctx) => bool | undefined. Return a boolean to gate Continue on a
    //                  custom step; undefined defers to the built-in validation.
    //   extraParams    merged into the submit payload.
    //   onCustomSubmit (params) => Promise. Replaces the built-in create request entirely.
    steps = null,
    renderStep = null,
    isStepValid = null,
    extraParams = null,
    onCustomSubmit = null,
    submitLabel = null,
    headerTitle = null,
    headerSubtitle = null,
    successTitle = null,
    successMessage = null,
    onSuccess,
    onClose,
}) => {
    const {auth} = useAuth();
    const queryClient = useQueryClient();
    const formRef = useRef(null);
    const [isInitialing, setIsInitialing] = useState(true);

    const isRehire = isRehireType(type);
    const isSingleTalent = isSingleTalentType(type);
    const STEPS = useMemo(() => (steps?.length ? steps : stepsFor(type)), [steps, type]);

    // Use ProjectFormProvider hook
    const {
        project,
        formModel: projectFormModel,
        isLoading: isProjectLoading,
        isProjectUpdating,
        onPositionChange,
        onFormChange: onProjectFormChange,
        onFormSubmit: onProjectFormSubmit,
    } = useProjectForm();

    // Use PositionFormProvider hook
    const {
        position,
        formModel: positionFormModel,
        isLoading: isPositionLoading,
        isDisabled: isPositionDisabled,
        hasFormBeenSubmitted,
        onFormChange: onPositionFormChange,
        isProjectUpdating: isPositionProjectUpdating,
        onFormReset: onPositionFormReset,
        onFormSubmit: onPositionFormSubmit,
    } = usePositionForm();

    // Step navigation
    const [currentStep, setCurrentStep] = useState(0);

    // Contract type from project form model
    const contractType = projectFormModel?.contract_type;

    const [submitError, setSubmitError] = useState(null);
    const [createdProject, setCreatedProject] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const isSubmitting = isProjectUpdating || isPositionProjectUpdating;
    const isLoading = isProjectLoading || isPositionLoading;

    // Fetch talent candidate data for the flows that target a single talent user
    const {
        data: candidate,
        isLoading: candidateLoading,
        error,
        refetch
    } = useAPI([
        'fetchTalent', {
            profileId: isSingleTalent ? data.id : null,
        }
    ], {
        enabled: isSingleTalent && !isLoading && !TB.isNaN(parseInt(data.id)),
        select: (responseData) => TalentCandidate.make({...data, ...responseData})
    })

    // For talent pool, use data directly
    const effectiveData = isSingleTalent ? candidate : data;

    // Reset form when stepping between steps
    useEffect(() => {
        if (formRef?.current?.isFormSubmitted?.()) {
            formRef?.current?.forceReset();
        }
    }, [currentStep]);

    // Scroll modal to top on step change
    useEffect(() => {
        const timer = setTimeout(() => {
            document.querySelector('.ant-modal-wrap')?.scrollTo({top: 0, behavior: 'smooth'});
        }, 200);
        return () => clearTimeout(timer);
    }, [currentStep]);

    // ─── onChange handler (similar to PositionWizard) ───
    const onChange = (params = {}) => {
        if (isSubmitting) return;

        let update = {...positionFormModel, ...params};

        // Reset end date if start date moves ahead
        if (update.ended_at || update.started_at) {
            let updatedEndDate = TB.moment(update.ended_at);
            let currentStartDate = TB.moment(update.started_at || positionFormModel?.started_at);
            if (!updatedEndDate.isValid() || TB.moment(currentStartDate).isSameOrAfter(updatedEndDate, 'day')) {
                update.ended_at = null;
            }
        }

        // Format avg_hours_per_period to integer
        if (params.avg_hours_per_period && params.avg_hours_per_period !== positionFormModel.avg_hours_per_period) {
            update.avg_hours_per_period = parseInt(params.avg_hours_per_period);
        }

        // Recalculate avg hours from budget
        if (params.budget && params.budget !== positionFormModel.budget) {
            let weeks = positionFormModel.calculatedTotalWeeks?.() || 1;
            const platformDisciplineRate = params.target_rate || TB.max(
                TB.map(positionFormModel.disciplines, dis =>
                    UserDiscipline.rateForDiscipline(dis.id, parseFloat(positionFormModel.percent_markup || 0), true)
                )
            );
            if (platformDisciplineRate) {
                let avg_hours = Math.floor((params.budget / platformDisciplineRate) / weeks);
                if (avg_hours > 1) {
                    update.avg_hours_per_period = avg_hours;
                }
            }
        }

        // Reset on-site days if required_onsite turned off
        if (positionFormModel.required_onsite && TB.isDefined(params.required_onsite) && !params.required_onsite) {
            update.onsite_days_per_week = null;
        } else if (params.onsite_days_per_week) {
            update.onsite_days_per_week = parseInt(params.onsite_days_per_week);
        }

        // Remove total hours from deliverables
        if (params.deliverables?.length) {
            update.deliverables = TB.map(params.deliverables || [], deliverable => ({
                ...deliverable,
                total_hours: deliverable.total_hours || undefined
            }));
        }

        // Calculate end date for fixed retainer
        const contractTypeName = Project.contractTypeNameFor(contractType);
        if (contractTypeName === Project.ContractType.FixedRetainer) {
            update.ended_at = positionFormModel.calculateFixedRetainedEstimatedEndDate?.(
                update.started_at,
                update.payout_frequency,
                update.actual_total_periods
            );
            if (positionFormModel?.isFixedRetainerRateBased?.() && update?.payout_frequency === 'weekly' && update?.avg_hours_per_period > 40) {
                update.avg_hours_per_period = 40;
            }
        }

        onPositionFormChange(update);
    };

    // Initialize project and position defaults on mount
    useEffect(() => {
        let setupProject = async () => {
            try {
                let initialPosition = buildInitialPosition(effectiveData, searchFilters, type);

                const contractTypes = TB.invert(laravel.project_contract_types || {});
                const defaultContractType = parseInt(contractTypes['Time & Materials']) || null;

                let projectName;
                if (isRehire) {
                    projectName = `Engagement with ${data.first_name || data.job_title || 'Talent'}`;
                } else if (type === 'single-talent') {
                    projectName = `Project for ${data.job_title || data.first_name}`;
                } else {
                    projectName = `Req for ${data.name}`;
                }

                // Initialize project
                await onProjectFormChange({
                    name: projectName,
                    description: initialPosition.description,
                    location: initialPosition.location,
                    type: 2,
                    contract_type: defaultContractType,
                });

                // Initialize position
                await onPositionFormChange(initialPosition);

                return setIsInitialing(false)
            }catch (error) {
                console.log(error);
                await TB.showServerErrorToast();
            }
        }

        if (isInitialing && effectiveData && projectFormModel && positionFormModel) {
            setupProject()
        }
    }, [isInitialing, effectiveData, isLoading, positionFormModel?.id]);

    // ─── Rehire: the talent's standard bill rate ───
    //
    // Mirrors the rate on the talent's tile in the pool, so the number pre-filled here is the one
    // the client was just looking at when they clicked Rehire. standardPlatformRate() matters for
    // more than consistency: it returns any client-preferred rate negotiated for this talent ahead
    // of their list rate, and an in-network talent the client already works with regularly is
    // exactly who tends to have one. Both branches return a bill-rate-scale number, matching
    // target_rate.
    const talentStandardRate = useMemo(() => {
        if (!isRehire) return null;

        // Same helper buildInitialPosition() seeds target_rate from, so the rate quoted in the
        // banner can never drift from the one sitting in the input.
        return talentStandardBillRate(
            effectiveData,
            positionFormModel?.disciplines,
            positionFormModel?.percent_markup
        );
    }, [isRehire, effectiveData, positionFormModel?.disciplines, positionFormModel?.percent_markup]);

    // ─── Does the current pricing structure actually collect an hourly rate? ───
    // Time & Materials always does. A Fixed Retainer only does when it's priced off a rate rather
    // than a cost per period. Fixed Fee never does — it's a lump sum against deliverables.
    // Used to gate both the rate prefill and the banner announcing it, so we never advertise an
    // hourly rate on a form that has nowhere to put one.
    const collectsHourlyRate = useMemo(() => {
        const contractTypeName = Project.contractTypeNameFor(contractType);

        if (contractTypeName === Project.ContractType.TimeMaterials) return true;
        if (contractTypeName === Project.ContractType.FixedRetainer) {
            return !!positionFormModel?.isFixedRetainerRateBased?.();
        }
        return false;
    }, [contractType, positionFormModel?.target_rate]);

    // Tracks the rate we auto-applied so we never clobber a rate the client set themselves.
    // Stays null until we've applied one at least once.
    const autoAppliedRateRef = useRef(null);

    // ─── Rehire: default the agreed rate to the talent's standard rate ───
    useEffect(() => {
        if (!isRehire || isInitialing || isSubmitting) return;
        if (!talentStandardRate || !positionFormModel || !collectsHourlyRate) return;

        const currentRate = positionFormModel.target_rate;

        // Already showing the rate we want — nothing to do.
        if (Number(currentRate) === Number(talentStandardRate)) return;

        // Before our first application, an empty rate just means "not set yet" — fill it.
        // After it, the only value we're allowed to overwrite is the one we put there ourselves.
        //
        // This distinction matters: both the T&M "use the standard rate" radio and the retainer's
        // cost-per-period option work by setting target_rate to null. Treating null as "not set
        // yet" would re-apply the rate the instant the client picked either one, snapping the
        // control back and making those options impossible to select.
        const isUntouched = autoAppliedRateRef.current === null
            ? (currentRate === undefined || currentRate === null || currentRate === '')
            : Number(currentRate) === Number(autoAppliedRateRef.current);

        if (!isUntouched) return;

        autoAppliedRateRef.current = talentStandardRate;
        onPositionFormChange({
            ...positionFormModel,
            target_rate: talentStandardRate,
            // The rate isn't a negotiation target here, it's the agreed number — show it.
            target_rate_is_visible: true,
        });
    }, [isRehire, isInitialing, isSubmitting, talentStandardRate, collectsHourlyRate, positionFormModel?.target_rate]);

    // ─── Rehire: reset the rate when the pricing structure changes mid-flow ───
    //
    // A rate agreed for an hourly engagement doesn't carry meaning under a fixed fee, and a stale
    // target_rate left behind by a previous structure would otherwise ride along in the payload as
    // the "agreed" rate. On every structure change we re-derive: the talent's standard rate if the
    // new structure bills hourly, cleared if it doesn't, and the override memory is wiped so the
    // client starts fresh.
    //
    // Bumping pricingResetKey remounts PositionTiming afterwards. That matters because its rate
    // controls read target_rate on mount only — PositionTimeMaterialsForm picks "standard vs.
    // custom rate" in a useState initializer. Without the remount the radio would keep showing the
    // old choice while the underlying value had already changed.
    const previousContractTypeRef = useRef(undefined);
    const [pricingResetKey, setPricingResetKey] = useState(0);

    useEffect(() => {
        if (!isRehire || isInitialing || isSubmitting) return;
        if (!TB.isDefined(contractType) || !positionFormModel) return;

        // First pass after initialization — record the starting structure, don't treat it as a change.
        if (!TB.isDefined(previousContractTypeRef.current)) {
            previousContractTypeRef.current = contractType;
            return;
        }

        if (previousContractTypeRef.current === contractType) return;

        previousContractTypeRef.current = contractType;

        const nextRate = collectsHourlyRate ? (talentStandardRate || null) : null;
        autoAppliedRateRef.current = nextRate;

        onPositionFormChange({
            ...positionFormModel,
            target_rate: nextRate,
            target_rate_is_visible: true,
        });

        setPricingResetKey(key => key + 1);
    }, [isRehire, isInitialing, isSubmitting, contractType]);

    // ─── Check if a step has prefilled data ───
    const isStepPrefilled = (stepKey) => {
        switch (stepKey) {
            case 'start':
                return true;
            case 'basic-info':
                return positionFormModel?.title?.length > 0 &&
                       positionFormModel?.disciplines?.length > 0;
            case 'timing-and-budget':
                return positionFormModel?.hasSetTerms?.();
            case 'review':
                return true;
            default:
                return false;
        }
    };

    // ─── Step validation (lightweight) ───
    const isCurrentStepValid = useMemo(() => {
        const step = STEPS[currentStep];
        if (!step) return false;

        // A wrapper owning this step gets first say. Only a real boolean counts as an answer —
        // returning undefined lets a wrapper validate some of its steps and defer on the rest.
        const custom = isStepValid?.(step.key);
        if (TB.isBoolean(custom)) {
            return custom;
        }

        switch (step.key) {
            case 'start':
                return true;
            case 'basic-info':
                return positionFormModel?.title?.length > 0 &&
                       positionFormModel?.disciplines?.length > 0;
            case 'timing-and-budget':
                return positionFormModel?.hasSetTerms?.();
            case 'review':
                return true;
            default:
                return true;
        }
    }, [
        currentStep,
        STEPS,
        // Wrappers pass a fresh isStepValid each render as their own state changes, which is what
        // re-runs this and re-enables Continue.
        isStepValid,
        positionFormModel?.title,
        positionFormModel?.disciplines?.length,
        positionFormModel?.started_at,
        positionFormModel?.ended_at,
        positionFormModel?.budget,
        positionFormModel?.deliverables?.length,
        positionFormModel?.actual_total_periods,
        positionFormModel?.payout_amount,
        positionFormModel?.payout_frequency,
        positionFormModel?.avg_hours_per_period,
    ]);

    const isLastStep = currentStep === STEPS.length - 1;


    const handleNext = async () => {
        let invalidFields = await formRef.current?.forceValidate();

        if (invalidFields) {
            const node = ReactDOM.findDOMNode(invalidFields);

            $('.ant-modal-wrap')?.animate({scrollTop: $(node).offset().top}, 1000);
            return;
        }

        if (isLastStep) {
            return handleSubmit();
        }
        setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    };

    const handleBack = () => {
        if (currentStep === 0) {
            onClose?.();
            return;
        }
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    // ─── Submission ───
    const submitQuickCreateMutation = useMutation(
        ['quickCreateSubmit', type, type === 'single-talent' ? data?.id : data?.id],
        async () => {
            let params = positionFormModel.transformForUpdate();

            // Drop the "replace this line" prompt if the client left it in place. No-op once they
            // have written over it.
            if (isRehire && params.description) {
                params.description = stripRehireScopePrompt(params.description);
            }

            console.log('contract_type: projectFormModel?.contract_type,', projectFormModel?.contract_type)
            // add default project params
            params = {
                ...params,
                name: params.title,
                contract_type: projectFormModel?.contract_type,
                location: projectFormModel.location,
                start_range: TB.parseInt(TB.invert(laravel.project_start_ranges)["ASAP"] || 1),
                ...(extraParams || {}),
            }
            const clientOrgId = auth.clientOrg?.().id;

            // A wrapper can take over submission entirely — used by flows whose end state isn't
            // "create a BYO project", such as recording a Change Order against an existing one.
            // It resolves the same shape the built-in requests do.
            if (onCustomSubmit) {
                return onCustomSubmit(params);
            }

            let request;
            if (isSingleTalent) {
                const talentUserId = data.id;

                if (isRehire) {
                    // Same endpoint and same payload as a Quick Hire — the agreed bill rate rides
                    // along as `target_rate`, which transformForUpdate() already includes. The only
                    // additions are the flag that tells the backend the terms are already agreed
                    // (so it synthesizes the proposal and requests the SOW rather than waiting on
                    // the talent) and the pool this was initiated from, for attribution.
                    params = {
                        ...params,
                        is_rehire: 1,
                        talent_pool_id: talentPool?.id ?? undefined,
                    };
                }

                request = TB.request(
                    TB.api_endpoint(`/api/clients/${clientOrgId}/talent-users/${talentUserId}/byo-projects/create`),
                    params
                );
            } else if (type === 'talent-pool') {
                // For talent pool, we'll create a project and invite all talent users
                const talentPoolId = data.id;
                request = TB.request(
                    TB.api_endpoint(`/api/clients/${clientOrgId}/talent-pools/${talentPoolId}/byo-projects/create`),
                    params
                );
            }

            request = request.headers({
                'Content-Type': 'multipart/form-data',
            });
            const response = await request.post();

            if (!TB.has(response, 'success')) {
                throw new Error('Something went wrong. Please try again.');
            }

            return response;
        },
        {
            onSuccess: async response => {
                // The built-in create resolves the API envelope; a wrapper's onCustomSubmit
                // resolves whatever its own flow produced and may hand back the project directly.
                // Read the envelope when it's there rather than assuming it, since assuming it is
                // what made a successful custom submit throw on `success.data`.
                const rawProject = TB.get(response, 'success.data.project');
                const project = rawProject
                    ? Project.make(rawProject)
                    : (response?.project || null);

                const clientOrgId = auth.clientOrg?.().id;

                if (type === 'talent-pool') {
                    const talentPoolId = data.id;
                    await queryClient.invalidateQueries(['fetchTalentPoolActiveProjects', { clientId: clientOrgId, poolId: talentPoolId }])
                }

                if (isRehire && talentPool?.id) {
                    await queryClient.invalidateQueries(['fetchTalentPoolActiveProjects', { clientId: clientOrgId, poolId: talentPool.id }])
                }

                setCreatedProject(project);
                setShowSuccess(true);
                setSubmitError(null);
                // onSuccess?.(project);
            },
            onError: (error) => {
                console.error('QuickCreate submit error', error);
                setSubmitError('Something went wrong. Please try again later.');
            }
        }
    );

    const handleSubmit = async () => {
        setSubmitError(null);
        submitQuickCreateMutation.mutate();
    };

    // Calculate talent's average rate (only for single talent)
    const talentAverageRate = useMemo(() => {
        if (!isSingleTalent || !effectiveData?.matchedDisciplines) return null;
        if (!effectiveData?.matchedDisciplines?.(positionFormModel.disciplines)?.length) return null;
        const rates = TB.map(effectiveData.matchedDisciplines?.(positionFormModel.disciplines), d => d.platform_rate).filter(Boolean);
        if (!rates.length) return null;
        return TB.mean(rates);
    }, [type, effectiveData, positionFormModel.disciplines]);

    // Get all available disciplines for talent pool type
    const availableDisciplines = useMemo(() => {
        if (isSingleTalent) {
            return effectiveData?.talent_disciplines || [];
        } else if (type === 'talent-pool') {
            // Get all unique disciplines from all talent in the pool
            const allDisciplines = effectiveData?.talent_disciplines || [];
            return TB.uniqBy(allDisciplines, 'id');
        }
        return [];
    }, [type, effectiveData]);

    // What a wrapper's step bodies get to work with. Everything here is already computed for the
    // built-in steps; exposing it means a custom step can read the talent, the pool and the form
    // models, and write back through onChange, without the wrapper duplicating any of it.
    const stepContext = useMemo(() => ({
        type,
        data,
        talentPool,
        positionFormModel,
        projectFormModel,
        onChange,
        availableDisciplines,
        talentStandardRate,
        isSubmitting,
        StepHeader,
    }), [
        type, data, talentPool, positionFormModel, projectFormModel,
        availableDisciplines, talentStandardRate, isSubmitting,
    ]);

    const customStepBody = useMemo(() => {
        const step = STEPS[currentStep];
        if (!step || !renderStep) return null;

        return renderStep(step.key, stepContext) || null;
    }, [STEPS, currentStep, renderStep, stepContext]);

    let modalTitle = 'Quick Create Req';
    let modalSubtitle = 'Create req & invite talent pool';
    if (isRehire) {
        modalTitle = 'Rehire';
        modalSubtitle = 'Record an already-agreed engagement';
    } else if (type === 'single-talent') {
        modalTitle = 'Quick Hire';
        modalSubtitle = 'Create project & request proposal';
    }

    // A wrapper whose flow branches can retitle the header to match the branch the client picked.
    modalTitle = headerTitle || modalTitle;
    modalSubtitle = headerSubtitle || modalSubtitle;

    return (
        <div className="quick-hire-modal-content">
            {/* Header with Inline Wizard Progress on Right */}
            <div className={TB.mapClass('px-8 py-5 border-b bg-gradient-to-r', {
                'from-emerald-50 to-teal-50': isRehire,
                'from-purple-50 to-blue-50': !isRehire,
            })}>
                <div className="flex flex-row items-center justify-between gap-4 min-w-0">
                    {/* Left: Icon, Title, Data Info — shrinks/truncates so the wizard stays on one line */}
                    <div className="flex flex-row items-center gap-3 min-w-0 flex-shrink">
                        <div className={TB.mapClass('w-12 h-12 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0', {
                            'from-emerald-500 to-teal-500': isRehire,
                            'from-purple-500 to-blue-500': !isRehire,
                        })}>
                            <i className={TB.mapClass('text-white text-lg', {
                                'fas fa-handshake': isRehire,
                                'fas fa-bolt': !isRehire,
                            })}/>
                        </div>
                        <div className="flex flex-row items-center gap-3 min-w-0">
                            <div className="min-w-0">
                                <h3 className="text-xl font-bold mb-0 leading-tight whitespace-nowrap">{modalTitle}</h3>
                                <p className="text-xs text-gray-500 mb-0 leading-tight truncate">
                                    {modalSubtitle}
                                </p>
                            </div>
                            <div className={TB.mapClass('h-10 w-px mx-1 flex-shrink-0', {
                                'bg-emerald-300': isRehire,
                                'bg-purple-300': !isRehire,
                            })}/>

                            {isSingleTalent ? (
                                <>
                                    <Img
                                        loader={<div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse"/>}
                                        unloader={<UserImageCircle user={data} hideTooltip={true} size={40} className="img-grayscale img-fluid"/>}
                                        src={data?.profile_image_path}
                                        className="w-10 h-10 rounded-full object-cover border-2 border-purple-200 flex-shrink-0"
                                    />
                                    <div className="flex flex-col justify-center min-w-0">

                                        <p className="text-sm font-semibold mb-0 leading-tight truncate">
                                            {data?.firstNamePlusInitial?.() || `${data?.first_name} ${data?.last_name?.[0]}.`}
                                        </p>
                                        {data?.location && (
                                            <p className="text-xs text-gray-600 mb-0 leading-tight truncate">
                                                <i className="fa fa-map-marker-alt mr-1"/>
                                                {data.location.city}{data.location.state && `, ${data.location.state}`}
                                            </p>
                                        )}
                                        {isRehire && (
                                            <p className="text-xs text-emerald-700 font-medium mb-0 leading-tight mt-0.5 truncate">
                                                <i className="fas fa-user-check mr-1"/>
                                                In-network{talentPool?.name ? ` · ${talentPool.name}` : ''}
                                            </p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-col justify-center min-w-0">
                                    <p className="text-sm font-semibold mb-0 leading-tight truncate">
                                        {data?.name}
                                    </p>
                                    <p className="text-xs text-gray-600 mb-0 leading-tight truncate">
                                        <i className="fa fa-users mr-1"/>
                                        {data?.talent_users_count || 0} talent members
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Inline Wizard Progress — never shrinks, so it always renders on one line */}
                    <div className="flex items-center gap-2 justify-end flex-shrink-0">
                        {STEPS.map((step, index) => (
                            <React.Fragment key={step.key}>
                                <div className="flex items-center gap-2">
                                    <div
                                        className={TB.mapClass('w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs transition-all flex-shrink-0', {
                                            'bg-purple-600 text-white': index === currentStep || index < currentStep,
                                            'bg-white text-gray-400 border-2 border-gray-300': index > currentStep,
                                            'cursor-pointer hover:bg-purple-700': index < currentStep,
                                        })}
                                        onClick={() => {
                                            if (index < currentStep) {
                                                setCurrentStep(index);
                                            }
                                        }}
                                    >
                                        {index < currentStep ? (
                                            <i className="fas fa-check text-xs"/>
                                        ) : (
                                            <span>{index + 1}</span>
                                        )}
                                    </div>
                                    {/* Labels are the first thing to go on narrow viewports */}
                                    <p className={TB.mapClass('text-xs font-medium mb-0 whitespace-nowrap hidden lg:block', {
                                        'text-purple-700 font-semibold': index === currentStep,
                                        'text-gray-700': index < currentStep,
                                        'text-gray-400': index > currentStep,
                                    })}>
                                        {step.label}
                                    </p>
                                </div>
                                {index < STEPS.length - 1 && (
                                    <div className="w-6 h-0.5 bg-gray-300 relative flex-shrink-0">
                                        <div
                                            className="absolute h-full bg-purple-600 transition-all"
                                            style={{
                                                width: index < currentStep ? '100%' : '0%'
                                            }}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{minHeight:400}}>
                {/* Step Content */}
                <SpinLoader isLoading={isInitialing || submitQuickCreateMutation.isLoading} className="px-6 py-6 min-h-96">
                    {showSuccess && createdProject ? (
                        <SuccessView
                            type={type}
                            data={data}
                            project={createdProject}
                            titleOverride={successTitle}
                            messageOverride={successMessage}
                            onClose={onClose}
                        />
                    ) : !isInitialing && positionFormModel && (
                        <Form
                            ref={formRef}
                            showRequired={false}
                            model={{...positionFormModel}}
                            formProps={{
                                onKeyDown: (e) => (e.key === 'Enter' || e.keyCode === 13) && e.preventDefault()
                            }}
                            onChange={(model) => {
                                let updates = {...positionFormModel};
                                for (let [key,value] of Object.entries(model)) {
                                    if (key !== 'description') {
                                        updates = TB.set(updates, key, value)
                                    }
                                }
                                return !isInitialing && onChange(model);
                            }}
                            mapping={mapped => ({
                                ...mapped,
                                started_at: mapped.started_at ? TB.moment(mapped.started_at).toISOString() : undefined,
                                ended_at: mapped.ended_at ? TB.moment(mapped.ended_at).toISOString() : undefined,
                            })}
                            submitButton={false}
                            cancelButton={false}
                            validateBeforeSubmit={true}
                            minimal={true}
                            onSubmit={() => handleNext()}
                            fields={[]}
                            isDisabled={submitQuickCreateMutation.isLoading}
                        >
                            {/*
                              * A wrapper's step body wins for any key it claims. Returning null
                              * from renderStep falls through to the built-in steps below, so a
                              * wrapper can replace 'start' while still reusing 'basic-info',
                              * 'timing-and-budget' and 'review' as they are.
                              */}
                            {customStepBody}

                            {!customStepBody && STEPS[currentStep].key === 'start' && <StartPage type={type} data={data} talentPool={talentPool} />}
                            {!customStepBody && STEPS[currentStep].key === 'basic-info' && (
                                <BasicInfoStep
                                    type={type}
                                    data={data}
                                    disciplines={availableDisciplines}
                                    onChange={onChange}
                                />
                            )}
                            {!customStepBody && STEPS[currentStep].key === 'timing-and-budget' && (
                                <div className="flex flex-col gap-4">
                                    <StepHeader
                                        step={3}
                                        icon={isRehire ? 'fas fa-file-invoice-dollar' : 'fas fa-clock'}
                                        iconClass="bg-blue-100 text-blue-600"
                                        title={isRehire ? 'Record the Agreed Terms' : 'Set Budget & Timeline'}
                                        description={isRehire
                                            ? `Enter the terms you and ${data?.first_name || 'this talent'} already agreed to.${collectsHourlyRate ? ' The bill rate is pre-filled with their standard rate — change it if you agreed to something different.' : ''}`
                                            : `Establish your ${nounFor(type)} budget, contract type, and expected start and end dates.`
                                        }
                                    />
                                    {isRehire && talentStandardRate && collectsHourlyRate && (
                                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-start gap-3">
                                            <i className="fas fa-info-circle text-emerald-600 mt-0.5"/>
                                            <p className="text-sm text-emerald-900 mb-0">
                                                Defaulted to <span className="font-semibold">${talentStandardRate}/hr</span> —{' '}
                                                {data?.first_name || 'this talent'}'s standard bill rate, the same one shown
                                                on their profile. Adjust it below to match your agreement.
                                            </p>
                                        </div>
                                    )}
                                    {/*
                                      * key: remounts the timing forms after a rehire pricing-structure
                                      * reset so their mount-time rate state re-derives. Constant for
                                      * every other flow, so nothing else remounts.
                                      */}
                                    <PositionTiming
                                        key={`position-timing-${pricingResetKey}`}
                                        hideLabel
                                        isPreAgreed={isRehire}
                                        labelClassName="text-base text-left text-black"
                                    />
                                </div>
                            )}
                            {!customStepBody && STEPS[currentStep].key === 'review' && <ReviewStep type={type} data={data} talentPool={talentPool} />}
                        </Form>
                    )}
                </SpinLoader>
            </div>

            {/* Error message */}
            {submitError && !showSuccess && (
                <div className="px-8 pb-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm text-center">
                        <i className="fas fa-exclamation-circle mr-2"/>
                        {submitError}
                    </div>
                </div>
            )}

            {/* Footer Actions */}
            {!showSuccess && (
                <div className="px-8 py-4 bg-gray-50 border-t flex flex-row items-center justify-between">
                    <button
                        type="button"
                        className="btn btn-dark"
                        disabled={submitQuickCreateMutation.isLoading || (isSingleTalent && candidateLoading)}
                        onClick={handleBack}
                    >
                        <i className="fas fa-arrow-left mr-2"/>
                        {currentStep === 0 ? 'Cancel' : 'Back'}
                    </button>

                    <div className="flex flex-row items-center gap-3">
                        <span className="text-sm text-gray-500">
                            Step {currentStep + 1} of {STEPS.length}
                        </span>
                        <button
                            type="button"
                            className={TB.mapClass('btn', {
                                'btn-success': isLastStep,
                                'btn-primary': !isLastStep,
                            })}
                            disabled={submitQuickCreateMutation.isLoading || !isCurrentStepValid || (isSingleTalent && candidateLoading)}
                            onClick={handleNext}
                        >
                            {submitQuickCreateMutation.isLoading ? (
                                <span className="flex flex-row items-center gap-2">
                                    <LoadingSpinner size="sm"/>
                                    {isRehire ? 'Recording...' : 'Creating...'}
                                </span>
                            ) : isLastStep ? (
                                <span>
                                    <i className={TB.mapClass('mr-2', {
                                        'fas fa-file-signature': isRehire,
                                        'fas fa-paper-plane': !isRehire,
                                    })}/>
                                    {/* A wrapper whose flow ends somewhere other than "create a
                                        project" can name its own action. */}
                                    {submitLabel
                                        || (isRehire
                                            ? 'Record Rehire & Request SOW'
                                            : (type === 'single-talent' ? 'Send Proposal Request' : 'Create Req & Invite Pool'))}
                                </span>
                            ) : (
                                <span>
                                    Continue
                                    <i className="fas fa-arrow-right ml-2"/>
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};


/**
 * Compact header shown at the top of each form step
 */
const StepHeader = ({icon, iconClass, title, description, step}) => (
    <div className="flex items-center gap-3 pb-3 mb-1 border-b border-gray-100">
        {/*<div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconClass}`}>*/}
        {/*    <i className={`${icon} text-sm`}/>*/}
        {/*</div>*/}
        <div>
            <p className="text-xs text-gray-600 mb-1">Step {step} of 4</p>

            <p className="text-base font-semibold text-gray-800 mb-0.5">{title}</p>
            <p className="text-xs text-gray-500 mb-0 leading-snug">{description}</p>
        </div>
    </div>
);

/**
 * Start Page — explains Quick Create and sets expectations
 */
const StartPage = ({type, data, talentPool}) => {
    const [isVisible, setIsVisible] = useState(false);
    const isRehire = isRehireType(type);
    const noun = nounFor(type);

    useEffect(() => {
        // Trigger animation after mount
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const talentDisplayName = data?.firstNamePlusInitial?.() || `${data?.first_name} ${data?.last_name?.[0]}.`;

    let title = 'Welcome to Quick Create Req';
    let description = `Create a requisition for ${data?.name} talent pool. This wizard will guide you through creating a req and inviting all matching pool members.`;

    if (isRehire) {
        title = `Rehire ${talentDisplayName}`;
        description = `Already agreed to work with ${talentDisplayName} outside of Wripple? Record it here and we'll handle the rest — Employer of Record, the SOW, invoicing and payments. There's no proposal round-trip: you're documenting terms you've both already settled on.`;
    } else if (type === 'single-talent') {
        title = 'Welcome to Quick Hire';
        description = `Fast-track your hiring process with ${talentDisplayName}. This wizard will guide you through creating a project and sending a proposal request in just a few simple steps.`;
    }

    return (
        <div
            className={TB.mapClass('flex flex-col items-center justify-center py-8 transition-all duration-700', {
                'opacity-100 scale-100': isVisible,
                'opacity-0 scale-95': !isVisible,
            })}
        >
            {/* Flow Icon */}
            <div className="relative mb-6">
                <div
                    className={TB.mapClass('w-24 h-24 rounded-full bg-gradient-to-br flex items-center justify-center shadow-2xl transition-all duration-700', {
                        'from-emerald-500 to-teal-600': isRehire,
                        'from-purple-500 to-blue-600': !isRehire,
                        'scale-100 rotate-0': isVisible,
                        'scale-0 rotate-180': !isVisible,
                    })}
                >
                    <i className={TB.mapClass('text-white text-4xl', {
                        'fas fa-handshake': isRehire,
                        'fas fa-bolt': !isRehire,
                    })}/>
                </div>
                {/* Animated ring */}
                <div
                    className={TB.mapClass('absolute inset-0 w-24 h-24 rounded-full border-4 transition-all duration-1000', {
                        'border-emerald-400': isRehire,
                        'border-purple-400': !isRehire,
                        'scale-150 opacity-0': isVisible,
                        'scale-100 opacity-100': !isVisible,
                    })}
                />
            </div>

            {/* Welcome Message */}
            <div
                className={TB.mapClass('text-center mb-8 transition-all duration-700 delay-200', {
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {title}
                </h3>
                <p className="text-gray-600 text-base max-w-2xl mx-auto leading-relaxed">
                    {description}
                </p>
            </div>

            {/* Info Cards */}
            <div
                className={TB.mapClass('grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mb-8 transition-all duration-700 delay-300', {
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                {/* Card 1: Role Requirements */}
                <div className="bg-white border border-purple-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-user-tag text-purple-600 text-xl"/>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">
                        {isRehire ? 'Confirm the Role' : 'Define the Role'}
                    </h4>
                    <p className="text-sm text-gray-600">
                        {isRehire
                            ? `Pick which of ${data?.first_name || 'their'}'s Wripple Roles this work falls under, and describe the scope you agreed to`
                            : `Specify the role title, job description, and required wripple roles for your ${noun}`
                        }
                    </p>
                </div>

                {/* Card 2: Budget & Timing */}
                <div className="bg-white border border-blue-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                        <i className={TB.mapClass('text-blue-600 text-xl', {
                            'fas fa-file-invoice-dollar': isRehire,
                            'fas fa-clock': !isRehire,
                        })}/>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">
                        {isRehire ? 'Record the Agreed Terms' : 'Set Budget & Timeline'}
                    </h4>
                    <p className="text-sm text-gray-600">
                        {isRehire
                            ? 'Enter the rate, pricing structure and dates you already agreed to — the rate is pre-filled with their standard rate'
                            : `Establish your ${noun} budget, contract type, and expected start and end dates`
                        }
                    </p>
                </div>

                {/* Card 3: Send Request */}
                <div className="bg-white border border-green-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i className={TB.mapClass('text-green-600 text-xl', {
                            'fas fa-file-signature': isRehire,
                            'fas fa-paper-plane': !isRehire,
                        })}/>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-2">
                        {isRehire ? 'Review & Record' : 'Review & Send'}
                    </h4>
                    <p className="text-sm text-gray-600">
                        {isRehire
                            ? 'Confirm the details — we skip the proposal step and go straight to requesting the SOW'
                            : `Review all details and ${type === 'single-talent' ? 'send your proposal request directly to the talent' : 'create req and invite all pool members'}`
                        }
                    </p>
                </div>
            </div>

            {/* Rehire: set expectations about the skipped proposal step */}
            {isRehire && (
                <div
                    className={TB.mapClass('bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 w-full max-w-4xl mb-8 flex items-start gap-3 transition-all duration-700 delay-300', {
                        'opacity-100 translate-y-0': isVisible,
                        'opacity-0 translate-y-4': !isVisible,
                    })}
                >
                    <i className="fas fa-forward text-emerald-600 mt-1"/>
                    <p className="text-sm text-emerald-900 mb-0">
                        <span className="font-semibold">No proposal needed.</span> Because you've already agreed on
                        scope and rate, {data?.first_name || 'this talent'} won't be asked to bid. They'll be notified
                        to confirm the terms you record here and this {noun} moves straight to
                        <span className="font-semibold"> SOW Requested</span>.
                    </p>
                </div>
            )}

            {/* What You'll Need */}
            <div
                className={TB.mapClass('bg-gradient-to-r border rounded-xl p-6 w-full max-w-2xl transition-all duration-700 delay-500', {
                    'from-emerald-50 to-teal-50 border-emerald-200': isRehire,
                    'from-purple-50 to-blue-50 border-purple-200': !isRehire,
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className={TB.mapClass('fas fa-list-check', {
                        'text-emerald-600': isRehire,
                        'text-purple-600': !isRehire,
                    })}/>
                    What You'll Need
                </h4>
                <div className="space-y-3">
                    <div className="flex items-start gap-3">
                        <i className="fas fa-check-circle text-green-500 mt-1"/>
                        <p className="text-sm text-gray-700 mb-0">
                            {isRehire ? (
                                <>
                                    <span className="font-semibold">The Agreed Scope:</span> Job title, the Wripple Role
                                    it falls under, and a description of the work
                                </>
                            ) : (
                                <>
                                    <span className="font-semibold">Role Requirements:</span> Job title and description
                                </>
                            )}
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <i className="fas fa-check-circle text-green-500 mt-1"/>
                        <p className="text-sm text-gray-700 mb-0">
                            {isRehire ? (
                                <>
                                    <span className="font-semibold">The Agreed Timeline:</span> Start and end dates you
                                    settled on
                                </>
                            ) : (
                                <>
                                    <span className="font-semibold">Timeline:</span> Estimated start and end dates for the engagement
                                </>
                            )}
                        </p>
                    </div>
                    <div className="flex items-start gap-3">
                        <i className="fas fa-check-circle text-green-500 mt-1"/>
                        <p className="text-sm text-gray-700 mb-0">
                            {isRehire ? (
                                <>
                                    <span className="font-semibold">The Agreed Rate:</span> Pre-filled with their standard
                                    rate — override it if you negotiated something else
                                </>
                            ) : (
                                <>
                                    <span className="font-semibold">Budget Information:</span> Your {noun} budget and preferred payment structure
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* Estimated Time */}
            <div
                className={TB.mapClass('mt-6 text-center transition-all duration-700 delay-700', {
                    'opacity-100': isVisible,
                    'opacity-0': !isVisible,
                })}
            >
                <p className="text-xs text-gray-500">
                    <i className={TB.mapClass('fas fa-clock mr-1', {
                        'text-emerald-500': isRehire,
                        'text-purple-500': !isRehire,
                    })}/>
                    Estimated time: {isRehire ? '2-3' : '3-5'} minutes
                </p>
            </div>
        </div>
    );
};

/**
 * Step 1: Basic Info — role title, description, and disciplines
 */
const BasicInfoStep = ({type, data, disciplines, onChange}) => {
    const isRehire = isRehireType(type);
    const isSingleTalent = isSingleTalentType(type);
    const noun = nounFor(type);

    const {
        formModel: projectFormModel,
        onFormChange: onProjectFormChange,
    } = useProjectForm();

    const {
        formModel: positionFormModel,
        isDisabled,
    } = usePositionForm();

    // State for showing all disciplines or just the first 15
    const [showAllDisciplines, setShowAllDisciplines] = useState(false);

    // Get selected discipline IDs for quick lookup
    const selectedDisciplineIds = TB.map(positionFormModel?.disciplines || [], 'id')

    const handleDisciplineToggle = (discipline) => {
        if (isDisabled) return;

        let newDisciplines = TB.xorBy(positionFormModel?.disciplines, [discipline], 'id');

        // Find matching templates based on selected disciplines
        const disciplineIds = TB.map(newDisciplines, 'id');

        const matchingTemplates = TB.filter(
            TB.flatMap(laravel.position_template_categories, 'templates'),
            template => {
                return TB.find(template.disciplines, dis => disciplineIds.includes(dis.id));
            }
        ) || [];

        // Update both disciplines and templates
        onChange({
            disciplines: newDisciplines,
            templates: matchingTemplates
        });
    };

    const displayName = isSingleTalent
        ? data?.firstNamePlusInitial?.()
        : data?.name;

    // For talent pools, calculate how many users match the selected disciplines
    const matchedUserCount = useMemo(() => {
        if (type !== 'talent-pool' || !data?.pool_disciplines) {
            return null;
        }

        return countUsersForDisciplines(data.pool_disciplines, selectedDisciplineIds);
    }, [type, data?.pool_disciplines, selectedDisciplineIds]);

    const totalPoolUsers = type === 'talent-pool' ? (data?.talent_users_count) : null;

    // Discipline display limit
    const INITIAL_DISPLAY_LIMIT = 15;

    // Sort and annotate disciplines with user counts for talent pool
    const sortedDisciplines = useMemo(() => {
        if (type !== 'talent-pool' || !data?.pool_disciplines) {
            return disciplines;
        }

        const poolDisciplines = data.pool_disciplines;

        // Add user count to each discipline and sort by selection status + count
        return TB.chain(disciplines)
            .map(discipline => {
                const userIds = poolDisciplines[discipline.id] || [];
                const userCount = userIds.length;
                const percentage = totalPoolUsers > 0 ? (userCount / totalPoolUsers) * 100 : 0;
                const isSelected = selectedDisciplineIds.includes(discipline.id);

                return {
                    ...discipline,
                    user_count: userCount,
                    percentage: percentage,
                    is_recommended: percentage > 50,
                    is_selected: isSelected
                };
            })
            .sortBy([
                d => !d.is_selected,  // Selected first (false comes before true)
                d => -d.user_count    // Then by user count descending
            ])
            .value();
    }, [type, disciplines, data?.pool_disciplines, totalPoolUsers]);

    // Determine which disciplines to display
    const displayedDisciplines = useMemo(() => {
        if (showAllDisciplines || sortedDisciplines.length <= INITIAL_DISPLAY_LIMIT) {
            return sortedDisciplines;
        }
        return sortedDisciplines.slice(0, INITIAL_DISPLAY_LIMIT);
    }, [sortedDisciplines, showAllDisciplines]);

    const hasMoreDisciplines = sortedDisciplines.length > INITIAL_DISPLAY_LIMIT;
    const remainingCount = sortedDisciplines.length - INITIAL_DISPLAY_LIMIT;

    return (
        <div className="flex flex-col gap-6">
            <StepHeader
                step={2}
                icon="fas fa-user-tag"
                iconClass="bg-purple-100 text-purple-600"
                title={isRehire ? 'Confirm the Role & Scope' : 'Define the Role'}
                description={isRehire
                    ? `Tell us which of ${displayName || 'this talent'}'s Wripple Roles this work falls under and describe the scope you already agreed to.`
                    : `Specify the role title, job description, and required skills for your ${noun}.`
                }
            />

            {/* Wripple Roles */}
            <div className="flex flex-row gap-4 items-start">
                <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-400 flex items-center justify-center text-xs font-semibold text-gray-700 flex-shrink-0">1</div>
                <div className="flex-1 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <label className="text-base text-left text-black">
                        Wripple Role
                    </label>
                    {type === 'talent-pool' && matchedUserCount !== null && (
                        <div className="flex items-center gap-2">
                            <span className={TB.mapClass('text-sm font-semibold px-3 py-1 rounded-full', {
                                'bg-green-100 text-green-700': matchedUserCount === totalPoolUsers,
                                'bg-yellow-100 text-yellow-700': matchedUserCount > 0 && matchedUserCount < totalPoolUsers,
                                'bg-red-100 text-red-700': matchedUserCount === 0,
                            })}>
                                <i className="fas fa-users mr-1"/>
                                {matchedUserCount} of {totalPoolUsers} members matched
                            </span>
                        </div>
                    )}
                </div>
                <p className="text-xs text-gray-600 mb-2">
                    {isRehire
                        ? `Choose the role(s) from ${displayName || 'this talent'}'s profile that match the work you agreed to. This sets their standard bill rate on the next step — nothing is preselected, so pick what actually applies.`
                        : type === 'single-talent'
                        ? `Choose which of ${displayName}'s role(s) you are looking for. Note: some have been preselected based on your matching search filters.`
                        : (
                            <>
                                Select the roles for this req. The minimum set has been preselected to match all {totalPoolUsers} talent members who would receive a request for proposal.
                                <span className="block mt-1">
                                    <i className="fas fa-star text-purple-500 mr-1"/>
                                    <span className="font-semibold text-purple-700">Recommended roles</span> (★) matching 50% or more of pool members.
                                </span>
                            </>
                        )
                    }
                </p>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-row flex-wrap gap-2 relative">
                        <FlipMove
                            duration={1000}
                            typeName={null}
                            maintainContainerHeight={false}
                        >
                            {displayedDisciplines?.map(discipline => {
                                const isSelected = selectedDisciplineIds.includes(discipline.id);
                                const isRecommended = discipline.is_recommended;

                                return (
                                    <div
                                        key={`dis-${discipline.id}`}
                                        className={TB.mapClass(
                                            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all',
                                            {
                                                // Selected state
                                                'bg-purple-600 text-white border-2 border-purple-600 shadow-md': isSelected && !isRecommended,
                                                'bg-purple-500 text-white border-2 border-purple-500 shadow-md': isSelected && isRecommended,

                                                // Unselected state
                                                'bg-purple-50 text-purple-700 border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-100': !isSelected && isRecommended,
                                                'bg-white text-gray-700 border-2 border-gray-300 hover:border-purple-400 hover:bg-purple-50': !isSelected && !isRecommended,

                                                'opacity-50 cursor-not-allowed': isDisabled,
                                            }
                                        )}
                                        onClick={() => handleDisciplineToggle(discipline)}
                                    >
                                        {isSelected && (
                                            <i className="fas fa-check-circle" />
                                        )}
                                        {!isSelected && isRecommended && (
                                            <i className="fas fa-star text-purple-500" />
                                        )}
                                        <span>{discipline.name}</span>
                                    </div>
                                );
                            })}
                        </FlipMove>
                    </div>

                    {hasMoreDisciplines && !showAllDisciplines && (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowAllDisciplines(true)}
                                className="text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline transition-colors"
                            >
                                Show {remainingCount} more role{remainingCount !== 1 ? 's' : ''}...
                            </button>
                        </div>
                    )}

                    {hasMoreDisciplines && showAllDisciplines && (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowAllDisciplines(false)}
                                className="text-sm text-gray-600 hover:text-gray-700 font-medium hover:underline transition-colors"
                            >
                                Show less
                            </button>
                        </div>
                    )}
                </div>

                {selectedDisciplineIds.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                        Please select at least one role
                    </p>
                )}
                </div>
            </div>

            {/* Position Title */}
            <div className="flex flex-row gap-4 items-start">
                <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-400 flex items-center justify-center text-xs font-semibold text-gray-700 flex-shrink-0">2</div>
                <div className="flex-1 flex flex-col gap-2">
                <label className="text-base text-left text-black">
                    Job Title
                </label>
                <p className="text-xs text-gray-600 mb-2">
                    {isRehire
                        ? 'What should we call this engagement? This is the title that appears on the SOW — we\'ve prefilled a default, swap it for something specific to the work.'
                        : 'What would you call this role or position?'
                    }
                </p>
                <FormInputComponent
                    fieldName="title"
                    type="antd-input"
                    className="mb-0"
                    label={false}
                    required={true}
                    disabled={isDisabled}
                    validationErrors={{
                        isDefaultRequiredValue:"Position name is required."
                    }}
                    value={positionFormModel.title || ''}
                >
                    <Input
                        type="text"
                        placeholder={isRehire ? 'e.g. Q3 Brand Campaign Design' : 'Open position name'}
                        style={{border: '1px solid #ececec', backgroundColor: '#F7F8FA'}}
                        autoComplete="off"
                        value={positionFormModel.title}
                        disabled={isDisabled}
                        maxLength={85}
                    />
                </FormInputComponent>
                </div>
            </div>

            {/* Position Description and document */}
            <div className="flex flex-row gap-4 items-start">
                <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-400 flex items-center justify-center text-xs font-semibold text-gray-700 flex-shrink-0">3</div>
                <div className="flex-1 flex flex-col [&_.editor-input]:min-h-[unset] [&_.editor-container]:mt-0">
                <div className="flex flex-col gap-2">
                    <label className="text-base text-left text-black">
                        {isRehire ? 'Scope of Work' : 'Job Description'}
                    </label>
                    <p className="text-xs text-gray-600">
                        {isRehire
                            ? 'What work did you agree to? This becomes the scope of work on the SOW. The boilerplate below flags this as a pre-agreed engagement — keep it and add your specific deliverables.'
                            : 'What are the key responsibilities?'
                        }
                    </p>

                    <FormInputComponent
                        fieldName="description"
                        value={positionFormModel.description || ''}
                        type="editor"
                        className="mb-0"
                        rows={3}
                        label={false}
                        placeholder={isRehire
                            ? 'Describe the deliverables and responsibilities you already agreed to...'
                            : 'Describe the role and job requirements...'
                        }
                        maxLength={5000}
                        minLength={50}
                        enableScroll={true}
                        validation={{
                            rules: {
                                maxLength: 5000,
                            },
                            errors: {
                                maxLength: TB.trans('validation.max.string', {
                                    'attribute': 'description',
                                    'max': 5000
                                }),
                                minCharLength: TB.trans('validation.min.string', {
                                    'attribute': 'description',
                                    'min': 50
                                })
                            }
                        }}
                        required={true}
                        isDisabled={isDisabled}
                    />
                </div>

                <div className="mx-auto flex flex-col items-center max-w-xl [&_.btn-file]:h-20">
                    <FormInputComponent
                        fieldName="description_file"
                        value={positionFormModel.description_file}
                        type="file"
                        viewable={typeof positionFormModel.description_file?.path === 'string'}
                        disabled={isDisabled}
                        label={false}
                        instructions={(
                            <span className="flex flex-row gap-2 items-center">
                                <PaperClipOutlined />
                                <span>
                                    {isRehire
                                        ? 'Have the agreement or scope doc? — drag & drop or click to attach a PDF or doc (optional)'
                                        : 'Have a brief? — drag & drop or click to attach a PDF or doc (optional)'
                                    }
                                </span>
                            </span>
                        )}
                        required={false}
                        fileNameDisplay={() => {
                            return positionFormModel.description_file &&
                                typeof positionFormModel.description_file === 'object'
                                && positionFormModel.description_file?.name
                        }}
                    />
                </div>
                </div>
            </div>


        </div>
    );
};

/**
 * Step 3: Review — summary of the project
 */
const ReviewStep = ({type, data, talentPool}) => {
    const {auth} = useAuth();
    const isRehire = isRehireType(type);
    const isSingleTalent = isSingleTalentType(type);
    const noun = nounFor(type);

    const {
        formModel: projectFormModel,
    } = useProjectForm();

    const {
        formModel: positionFormModel,
    } = usePositionForm();

    const contractType = projectFormModel?.contract_type;
    const contractTypeName = Project.contractTypeNameFor(contractType);
    const projectDescription = positionFormModel?.description;
    const positionMarkup = positionFormModel.percent_markup || projectFormModel.percent_markup || 0

    const positionRate = useMemo(() => {
        if (positionFormModel.target_rate) return positionFormModel.target_rate;

        return TB.max(
            TB.map(positionFormModel.disciplines, dis => {
                    return UserDiscipline.rateForDiscipline(dis.id, parseFloat(positionMarkup), true)
                }
            ))
    }, [positionFormModel.disciplines, positionFormModel.target_rate, positionMarkup])

    // Calculate budget display
    const budgetDisplay = useMemo(() => {
        if (contractTypeName === Project.ContractType.TimeMaterials) {
            const hours = positionFormModel?.avg_hours_per_period || 0;
            return `${hours} hrs/week @ $${positionRate}/hr`;
        } else if (contractTypeName === Project.ContractType.FixedRetainer) {
            const amount = positionFormModel?.payout_amount || 0;
            const frequency = positionFormModel?.payout_frequency || 'weekly';
            const periods = positionFormModel?.actual_total_periods || 0;
            const getPeriodRetainer = () => {
                return positionFormModel.isFixedRetainerRateBased() ? (positionFormModel.target_rate * positionFormModel.avg_hours_per_period) : (positionFormModel.budget)
            }
            return `$${getPeriodRetainer()}/${frequency} for ${periods} ${frequency === 'weekly' ? 'weeks' : 'months'}`;
        } else if (contractTypeName === Project.ContractType.FixedFee) {
            const budget = positionFormModel?.budget || 0;
            return `$${budget} fixed fee`;
        }
        return 'Not set';
    }, [positionFormModel, contractTypeName]);

    // Format dates
    const startDate = positionFormModel?.started_at
        ? TB.moment(positionFormModel.started_at).format('MMM D, YYYY')
        : 'Not set';
    const endDate = positionFormModel?.ended_at
        ? TB.moment(positionFormModel.ended_at).format('MMM D, YYYY')
        : 'Not set';

    return (
        <div className="flex flex-col gap-6">
            <StepHeader
                step={4}
                icon={isRehire ? 'fas fa-file-signature' : 'fas fa-paper-plane'}
                iconClass="bg-green-100 text-green-600"
                title={isRehire ? 'Review & Record' : 'Review & Send'}
                description={isRehire
                    ? 'Check the terms below. Recording this skips the proposal step and moves straight to requesting the SOW.'
                    : `Review all details and ${type === 'single-talent' ? 'send your proposal request directly to the talent' : 'create req and invite all pool members'}.`
                }
            />

            {/* Rehire: what happens on submit */}
            {isRehire && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                    <h5 className="font-semibold text-base mb-3 flex items-center gap-2 text-emerald-900">
                        <i className="fas fa-route text-emerald-600"/>
                        What Happens Next
                    </h5>
                    <ol className="text-sm text-emerald-900 mb-0 pl-5 space-y-1 list-decimal">
                        <li>This engagement is created and {data?.first_name || 'the talent'} is added to it directly.</li>
                        <li>The proposal step is skipped — the terms below are recorded as already agreed.</li>
                        <li>The engagement moves to <span className="font-semibold">SOW Requested</span> and Wripple
                            prepares the SOW as Employer of Record.</li>
                    </ol>
                </div>
            )}

            {/* Project Info */}
            <div className="border rounded-lg p-4">
                <h5 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <i className="fas fa-briefcase text-purple-600"/>
                    {isRehire ? 'Agreed Role & Scope' : 'Role Details'}
                </h5>
                <div className="space-y-3">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">{isRehire ? 'Engagement Title' : 'Role Name'}</p>
                        <p className="text-sm font-medium mb-0">{positionFormModel?.title || 'Not set'}</p>
                    </div>
                    {positionFormModel.description && (
                        <div>
                            <p className="text-xs text-gray-500 mb-1">{isRehire ? 'Scope of Work' : 'Description'}</p>
                            <span className="text-sm rich-text-display">
                                {/* Preview what will actually be recorded — without the guidance prompt */}
                                <span dangerouslySetInnerHTML={{__html: isRehire
                                    ? stripRehireScopePrompt(positionFormModel.description)
                                    : positionFormModel.description
                                }}/>
                            </span>
                        </div>
                    )}
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Wripple Roles</p>
                        <div className="flex flex-wrap gap-2">
                            {TB.map(positionFormModel?.disciplines || [], discipline => (
                                <span
                                    key={discipline.id}
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                >
                                    {discipline.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Skills grouped by importance */}
                    {positionFormModel?.skills?.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2">Skills</p>
                            <div className="flex flex-wrap gap-2">
                                {TB.map(positionFormModel.skills, skill => (
                                    <span
                                        key={`skill-${skill.id}`}
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                        {skill.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Industries grouped by importance */}
                    {positionFormModel?.industries?.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2">Industries</p>
                            <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                    {TB.map(positionFormModel?.industries, industry => (
                                        <span
                                            key={`industry-${industry.id}`}
                                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                        >
                                            {industry.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Brands - just show name */}
                    {positionFormModel?.brands?.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 mb-2">Brands</p>
                            <div className="flex flex-wrap gap-2">
                                {TB.map(positionFormModel.brands, brand => (
                                    <span
                                        key={`brand-${brand.id}`}
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                    >
                                        {brand.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Timing & Budget */}
            <div className="border rounded-lg p-4">
                <h5 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <i className="fas fa-clock text-purple-600"/>
                    {isRehire ? 'Agreed Timing & Pricing' : 'Timing & Budget'}
                </h5>
                <div className="space-y-3">
                    <div>
                        <p className="text-xs text-gray-500 mb-1">{isRehire ? 'Pricing Structure' : 'Contract Type'}</p>
                        <p className="text-sm font-medium mb-0">{contractTypeName}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-gray-500 mb-1">Start Date</p>
                            <p className="text-sm font-medium mb-0">{startDate}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 mb-1">End Date</p>
                            <p className="text-sm font-medium mb-0">{endDate}</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 mb-1">{isRehire ? 'Agreed Price' : 'Budget'}</p>
                        <p className="text-sm font-medium mb-0">{budgetDisplay}</p>
                    </div>
                </div>
            </div>

            {/* Target Info */}
            <div className="border rounded-lg p-4 bg-gray-50">
                <h5 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <i className={`fas ${isSingleTalent ? 'fa-user' : 'fa-users'} text-purple-600`}/>
                    {isRehire
                        ? 'Recording This Engagement With'
                        : (type === 'single-talent' ? 'Sending Proposal Request To' : 'Inviting Talent Pool')
                    }
                </h5>
                {isSingleTalent ? (
                    <div className="flex flex-row items-center gap-3">
                        {data?.image_path && (
                            <UserImageCircle
                                user={data}
                                size={48}
                                alt={`${data.first_name} ${data.last_name}`}
                                className="img-fluid grayscale"
                            />
                        )}
                        <div>

                                {/* <p>{talent.firstNamePlusInitial()}</p> */}
                            <p className="font-semibold mb-0">
                                {data?.first_name} {data?.last_name?.[0]}.
                            </p>
                            {data?.job_title && (
                                <p className="text-sm text-gray-600 mb-0">{data.job_title}</p>
                            )}
                            {isRehire && (
                                <p className="text-xs text-emerald-700 font-medium mb-0 mt-1">
                                    <i className="fas fa-user-check mr-1"/>
                                    In your network{talentPool?.name ? ` via ${talentPool.name}` : ''}
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="font-semibold mb-1">{data?.name}</p>
                        <p className="text-sm text-gray-600 mb-0">
                            Up to {data?.talent_users_count || 0} talent members from this pool will be invited to this req. Note: Only available matching talent will be invited
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Success View - shown after successful submission
 */
const SuccessView = ({type, data, project, titleOverride, messageOverride, onClose}) => {
    const [isVisible, setIsVisible] = useState(false);
    const isRehire = isRehireType(type);

    useEffect(() => {
        // Trigger animation after mount
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const talentDisplayName = data?.firstNamePlusInitial?.() || `${data?.first_name} ${data?.last_name?.[0]}.`;

    let successTitle = 'Req Created & Pool Invited Successfully!';
    let successMessage = `Your req has been created and ${project?.positions?.[0]?.matching_talent_count ? project?.positions[0]?.matching_talent_count : 'matching'} members of ${data?.name} have been invited. You can now visit the Project page to track responses and manage candidates.`;

    if (isRehire) {
        successTitle = 'Rehire Recorded — SOW Requested';
        successMessage = `Your engagement with ${talentDisplayName} has been recorded at the terms you agreed to, and the SOW has been requested. ${data?.first_name || 'They'}'ll be notified to confirm the terms. No proposal step needed.`;
    } else if (type === 'single-talent') {
        successTitle = 'Request for Proposal Sent Successfully!';
        successMessage = `Your proposal request has been sent to ${talentDisplayName}. You can now visit the Project page for updates or to view similar talent for the Role you requested.`;
    }

    // A wrapper whose flow ends somewhere other than "project created" supplies its own wording.
    successTitle = titleOverride || successTitle;
    successMessage = messageOverride || successMessage;

    return (
        <div
            className={TB.mapClass('flex flex-col items-center justify-center py-8 transition-all duration-700', {
                'opacity-100 scale-100': isVisible,
                'opacity-0 scale-95': !isVisible,
            })}
        >
            {/* Success Icon */}
            <div className="relative mb-6">
                <div
                    className={TB.mapClass('w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-2xl transition-all duration-700', {
                        'scale-100 rotate-0': isVisible,
                        'scale-0 rotate-180': !isVisible,
                    })}
                >
                    <i className="fas fa-check text-white text-4xl"/>
                </div>
                {/* Animated ring */}
                <div
                    className={TB.mapClass('absolute inset-0 w-24 h-24 rounded-full border-4 border-green-400 transition-all duration-1000', {
                        'scale-150 opacity-0': isVisible,
                        'scale-100 opacity-100': !isVisible,
                    })}
                />
            </div>

            {/* Success Message */}
            <div
                className={TB.mapClass('text-center mb-8 transition-all duration-700 delay-200', {
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {successTitle}
                </h3>
                <p className="text-gray-600 text-base max-w-lg mx-auto leading-relaxed">
                    {successMessage}
                </p>
            </div>

            {/* Project Info Card */}
            <div
                className={TB.mapClass('bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6 mb-8 w-full max-w-lg transition-all duration-700 delay-300', {
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-briefcase text-white text-xl"/>
                    </div>
                    <div className="flex-1">
                        <p className="text-xs text-purple-600 font-semibold mb-1 uppercase tracking-wide">
                            {isRehire ? 'Engagement Recorded' : (type === 'single-talent' ? 'Project Created' : 'Req Created')}
                        </p>
                        <h4 className="text-lg font-bold text-gray-900 mb-1">
                            {project?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                            {isRehire
                                ? 'Review the SOW, track signatures, and manage the engagement from here'
                                : (type === 'single-talent'
                                    ? 'Track proposal progress, communicate with talent, and manage your project'
                                    : 'Track responses, manage candidates, and communicate with pool members')
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div
                className={TB.mapClass('flex flex-col sm:flex-row gap-3 w-full max-w-lg transition-all duration-700 delay-500', {
                    'opacity-100 translate-y-0': isVisible,
                    'opacity-0 translate-y-4': !isVisible,
                })}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-outline-dark flex-1 py-3 font-semibold"
                >
                    Close and Continue
                </button>
                <Link
                    to={project?.detailLink?.()}
                    target="_blank"
                    className="btn btn-primary flex-1 text-center py-3 font-semibold shadow-lg hover:shadow-xl transition-shadow"
                >
                    <i className="fas fa-external-link-alt mr-2"/>
                    {isRehire ? 'Visit Engagement Details' : 'Visit Project Details'}
                </Link>
            </div>

            {/* Additional tip */}
            <div
                className={TB.mapClass('mt-6 text-center transition-all duration-700 delay-700', {
                    'opacity-100': isVisible,
                    'opacity-0': !isVisible,
                })}
            >
                <p className="text-xs text-gray-500">
                    <i className="fas fa-lightbulb mr-1 text-yellow-500"/>
                    {isRehire
                        ? `You'll be notified when the SOW is ready for signature`
                        : (type === 'single-talent'
                            ? `You'll receive notifications when ${data?.firstNamePlusInitial?.()} responds to your request`
                            : `You'll receive notifications when pool members respond to your req`)
                    }
                </p>
            </div>
        </div>
    );
};

export default QuickCreateProjectModal;